import {
	type ChangeEvent,
	type SelectHTMLAttributes,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

import { useRequest } from "~/hooks/useRequest";
import { getPlanDiscounts } from "~/modules/sales/services";
import type { Discount } from "~/modules/sales/types";
import type { Option } from "~/types/types";

interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
	label?: string;
	placeholder?: string;
	error?: string;
	onChange(value: any): void;
	value?: any;
	hint?: string;
	planId: string;
}

function normalizedValue(label: string) {
	return label
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replaceAll(" ", "-");
}

export const PlanDiscountsInput: React.FC<Props> = ({
	label,
	placeholder,
	error,
	onChange,
	hint,
	value,
	planId,
	...rest
}) => {
	const { data } = useRequest<Discount[]>({
		requestFn: () => getPlanDiscounts(planId),
	});

	const [options, setOptions] = useState<Option<string | undefined>[]>([]);
	const [internalValue, setInternalValue] = useState<any>();

	const inputRef = useRef<HTMLSelectElement>(null);

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLSelectElement>) => {
			const newValue = options.find(
				(o) => normalizedValue(o.label) === e.target.value,
			);
			setInternalValue(newValue?.value);
			onChange(newValue?.value);
		},
		[options, onChange],
	);

	useEffect(() => {
		if (data?.length) {
			const parsedOptions = data.reduce<Option<string | undefined>[]>(
				(accu, currVal) => {
					accu.push({
						label: currVal.code,
						value: currVal.code,
					});
					return accu;
				},
				[],
			);
			setOptions([...parsedOptions, { label: "Nenhum cupom", value: "" }]);
		}
	}, [data]);

	const isLoaded = useRef<boolean>(false);
	useEffect(() => {
		if (isLoaded.current) return;

		if (value && options.length) {
			const defaultValue = options.find((o) => o.value === value);
			if (inputRef.current) {
				inputRef.current.value = normalizedValue(defaultValue?.label ?? "");
				setInternalValue([defaultValue]);
			}
			isLoaded.current = true;
		}
	}, [value, options]);

	return (
		<div className="flex flex-col w-full gap-2">
			<p>{label}</p>
			{hint ? <small>{hint}</small> : null}
			<label
				data-filled={!!internalValue}
				data-error={!!error}
				className="flex w-full bg-white rounded-md border-2
          focus-within:border-gpa-blue-500 px-2 h-10
          data-[error=true]:border-gpa-red data-[filled=true]:text-black
          data-[filled=true]:border-gpa-blue-500"
			>
				<select
					ref={inputRef}
					defaultValue=""
					data-filled={!!internalValue}
					className="flex-1 outline-none w-full
            bg-transparent focus:text-black text-slate-500 text-opacity-55
            data-[filled=true]:text-black
          "
					onChange={handleChange}
					{...rest}
				>
					<option value="" disabled hidden>
						{placeholder}
					</option>

					{options.length ? (
						options.map((option) => (
							<option
								key={`option-${option.value}`}
								value={normalizedValue(option.label)}
							>
								{option.label}
							</option>
						))
					) : (
						<option>Carregando...</option>
					)}
				</select>
			</label>
			{error ? <p className="text-xs text-gpa-red">{error}</p> : null}
		</div>
	);
};
